import { create } from 'zustand';
import useLoadingStore from './use-loading';
import { makeRequest } from 'api/api-client';
import { HTTP } from 'constants/http-method';
import { API_ENDPOINTS } from 'api/endpoints';

const usePositionStore = create((set) => ({
  positionLoading: false,
  totalPositions: 0,
  positions: [],
  pagination: {
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
  },

  // Function to reset currentPage only when an action happens
  resetCurrentPage: () => {
    set((state) => ({
      pagination: {
        ...state.pagination,
        currentPage: 1,
      },
    }));
  },

  // Function to get position
  getPosition: async ({ query = null, successCallback }) => {
    set({
      positionLoading: true,
    });
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_POSITION,
        null,
        query
      );

      if (response.success) {
        set({
          totalPositions: response.data?.pagination?.totalPositions,
          positions: response.data?.data || [],
          pagination: {
            pageSize: response.data?.pagination?.limit,
            totalItems: response.data?.pagination?.total,
            currentPage: response.data?.pagination?.page,
            totalPages: response.data?.pagination?.totalPages,
          },
        });
        successCallback && successCallback();
      }
    } catch (error) {
    } finally {
      set({
        positionLoading: false,
      });
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to create the position
  createPosition: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.CREATE_POSITION,
        data
      );

      if (response.success) {
        await usePositionStore.getState().getPosition({
          successCallback: () => {
            usePositionStore.getState().resetCurrentPage(); // Reset page
            successCallback && successCallback(response);
          },
        });
      }

      // if (response.success) {
      //   set((state) => {
      //     const updatedPositions = [response?.data?.data, ...state.positions];
      //     const updatedTotalItems = updatedPositions.length;
      //     const updatedTotalPages = Math.ceil(
      //       updatedTotalItems / state.pagination.pageSize
      //     );

      //     return {
      //       positions: updatedPositions,
      //       pagination: {
      //         ...state.pagination,
      //         totalItems: updatedTotalItems,
      //         totalPages: updatedTotalPages,
      //       },
      //     };
      //   });
      //   successCallback && successCallback(response);
      // }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to delete position
  deletePosition: async ({ id, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.DELETE,
        API_ENDPOINTS.DELETE_POSITION,
        null,
        null,
        id
      );

      if (response.success) {
        set((state) => {
          const updatedPositions = state.positions.filter(
            (position) => position.id !== id
          );

          const updatedTotalItems = updatedPositions.length;
          const updatedTotalPages = Math.ceil(
            updatedTotalItems / state.pagination.pageSize
          );

          return {
            positions: updatedPositions,
            pagination: {
              ...state.pagination,
              totalItems: updatedTotalItems,
              totalPages: updatedTotalPages,
            },
          };
        });

        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to update position
  updatePosition: async ({ id, data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.UPDATE_POSITION,
        data,
        null,
        id
      );

      if (response.success) {
        set((state) => {
          // Find the updated position
          const updatedPosition = state.positions.find(
            (position) => position.id === id
          );

          // If the position is found, update it and move it to the first position
          if (updatedPosition) {
            const updatedPositions = [
              { ...updatedPosition, ...data }, // Updated position
              ...state.positions.filter((position) => position.id !== id), // Rest of the positions
            ];

            return {
              positions: updatedPositions,
              pagination: {
                ...state.pagination,
                currentPage: 1,
              },
            };
          }

          // If the position is not found, return the state as is
          return state;
        });

        successCallback && successCallback(response);
      }
    } catch (error) {
      // Handle error
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to active/inactive position
  togglePositionStatus: async ({ id, data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.ACTIVE_INACTIVE_POSITION,
        data,
        null,
        id
      );
      if (response.success) {
        set((state) => ({
          positions: state.positions.map((position) =>
            position.id === id ? { ...position, ...data } : position
          ),
        }));
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },
}));

export default usePositionStore;
